import {
  createAsyncThunk,
} from "@reduxjs/toolkit";

import {
  axiosConsultation,
} from "../../../api/axios";

import ENDPOINTS from "../../../api/endpoints";

/* =========================================================
   ERROR
========================================================= */

const getErrorPayload = (
  error,
  fallbackMessage
) => {
  const responseData =
    error?.response?.data;

  const backendMessage =
    responseData?.message;

  const message =
    Array.isArray(
      backendMessage
    )
      ? backendMessage.join(
          ", "
        )
      : backendMessage ||
        responseData?.error ||
        error?.message ||
        fallbackMessage;

  return {
    statusCode:
      error?.response?.status ||
      responseData
        ?.statusCode ||
      500,

    message,

    error:
      responseData?.error ||
      null,

    data:
      responseData ||
      null,
  };
};

const logApiError = (
  label,
  error
) => {
  console.group(
    `❌ ${label}`
  );

  console.error(
    "STATUS:",
    error?.response
      ?.status
  );

  console.error(
    "DATA:",
    error?.response
      ?.data
  );

  console.error(
    "MESSAGE:",
    error?.response
      ?.data
      ?.message
  );

  console.error(
    "FULL ERROR:",
    error
  );

  console.groupEnd();
};

/* =========================================================
   CREATE CONSULTATION
========================================================= */

export const createConsultation =
  createAsyncThunk(
    "consultation/create",

    async (
      consultationData,
      {
        rejectWithValue,
      }
    ) => {
      try {
        /*
         * IMPORTANT:
         * aucun changement d'ID ici.
         *
         * userId doit déjà être le vrai
         * ms-users user.id.
         */
        const payload = {
          ...consultationData,

          userId:
            String(
              consultationData
                ?.userId ||
                ""
            ).trim(),

          doctorId:
            String(
              consultationData
                ?.doctorId ||
                ""
            ).trim(),

          title:
            String(
              consultationData
                ?.title ||
                "Consultation"
            ).trim(),

          sections:
            Array.isArray(
              consultationData
                ?.sections
            )
              ? consultationData
                  .sections
              : [],
        };

        console.group(
          "📤 CREATE CONSULTATION"
        );

        console.log(
          "URL:",
          ENDPOINTS
            .CONSULTATION
            .BASE
        );

        console.log(
          "🔥 userId sent to backend:",
          payload.userId
        );

        console.log(
          "🔥 doctorId:",
          payload.doctorId
        );

        console.log(
          "PAYLOAD:",
          payload
        );

        console.log(
          "JSON:",
          JSON.stringify(
            payload,
            null,
            2
          )
        );

        console.groupEnd();

        /* ---------------------------------------------
           VALIDATION FRONT
        --------------------------------------------- */

        if (
          !payload.userId
        ) {
          return rejectWithValue(
            {
              statusCode:
                400,

              message:
                "userId patient manquant avant création de la consultation.",

              error:
                "INVALID_PATIENT_USER_ID",

              data:
                null,
            }
          );
        }

        if (
          !payload.doctorId
        ) {
          return rejectWithValue(
            {
              statusCode:
                400,

              message:
                "doctorId manquant avant création de la consultation.",

              error:
                "INVALID_DOCTOR_ID",

              data:
                null,
            }
          );
        }

        /* ---------------------------------------------
           API
        --------------------------------------------- */

        const response =
          await axiosConsultation.post(
            ENDPOINTS
              .CONSULTATION
              .BASE,

            payload
          );

        console.log(
          "✅ CREATE CONSULTATION RESPONSE:",
          response.data
        );

        return response.data;
      } catch (error) {
        logApiError(
          "CREATE CONSULTATION ERROR",
          error
        );

        return rejectWithValue(
          getErrorPayload(
            error,
            "Erreur lors de la création de la consultation"
          )
        );
      }
    }
  );

/* =========================================================
   GET BY ID
========================================================= */

export const getConsultationById =
  createAsyncThunk(
    "consultation/getById",

    async (
      id,
      {
        rejectWithValue,
      }
    ) => {
      try {
        const cleanId =
          String(
            id || ""
          ).trim();

        if (!cleanId) {
          return rejectWithValue(
            {
              statusCode:
                400,

              message:
                "Consultation ID manquant.",

              error:
                "INVALID_CONSULTATION_ID",
            }
          );
        }

        const response =
          await axiosConsultation.get(
            ENDPOINTS
              .CONSULTATION
              .BY_ID(
                cleanId
              )
          );

        return response.data;
      } catch (error) {
        logApiError(
          "GET CONSULTATION BY ID ERROR",
          error
        );

        return rejectWithValue(
          getErrorPayload(
            error,
            "Erreur lors de la récupération de la consultation"
          )
        );
      }
    }
  );

/* =========================================================
   GET BY PATIENT
========================================================= */

export const getConsultationsByPatient =
  createAsyncThunk(
    "consultation/getByPatient",

    async (
      payload,
      {
        rejectWithValue,
      }
    ) => {
      try {
        const rawUserId =
          typeof payload ===
          "object"
            ? payload
                ?.userId
            : payload;

        const rawDoctorId =
          typeof payload ===
          "object"
            ? payload
                ?.doctorId
            : undefined;

        const subprofileId =
          typeof payload === "object"
            ? payload?.subprofileId
            : undefined;

        const userId =
          String(
            rawUserId ||
              ""
          ).trim();

        const doctorId =
          rawDoctorId
            ? String(
                rawDoctorId
              ).trim()
            : undefined;

        if (!userId) {
          return rejectWithValue(
            {
              statusCode:
                400,

              message:
                "userId patient manquant.",

              error:
                "INVALID_PATIENT_USER_ID",
            }
          );
        }

        console.log(
          "📥 GET CONSULTATIONS:",
          {
            userId,
            doctorId,
          }
        );

        const response =
          await axiosConsultation.get(
            ENDPOINTS
              .CONSULTATION
              .BY_PATIENT(
                userId,
                doctorId
              ),
            subprofileId
              ? {
                  headers: {
                    "X-Subprofile-Id": String(subprofileId),
                    "X-Acting-Subprofile-Id": String(subprofileId),
                  },
                }
              : undefined
          );

        return response.data;
      } catch (error) {
        logApiError(
          "GET CONSULTATIONS BY PATIENT ERROR",
          error
        );

        return rejectWithValue(
          getErrorPayload(
            error,
            "Erreur lors de la récupération des consultations"
          )
        );
      }
    }
  );

/* =========================================================
   GET BY DOCTOR
========================================================= */

export const getConsultationsByDoctor =
  createAsyncThunk(
    "consultation/getByDoctor",

    async (
      doctorId,
      {
        rejectWithValue,
      }
    ) => {
      try {
        const cleanDoctorId =
          String(
            doctorId ||
              ""
          ).trim();

        if (
          !cleanDoctorId
        ) {
          return rejectWithValue(
            {
              statusCode:
                400,

              message:
                "doctorId manquant.",

              error:
                "INVALID_DOCTOR_ID",
            }
          );
        }

        const response =
          await axiosConsultation.get(
            ENDPOINTS
              .CONSULTATION
              .BY_DOCTOR(
                cleanDoctorId
              )
          );

        return response.data;
      } catch (error) {
        logApiError(
          "GET CONSULTATIONS BY DOCTOR ERROR",
          error
        );

        return rejectWithValue(
          getErrorPayload(
            error,
            "Erreur lors de la récupération des consultations du médecin"
          )
        );
      }
    }
  );

/* =========================================================
   UPDATE CONSULTATION
========================================================= */

export const updateConsultation =
  createAsyncThunk(
    "consultation/update",

    async (
      {
        consultationId,
        consultationData,
      },
      {
        rejectWithValue,
      }
    ) => {
      try {
        const cleanId =
          String(
            consultationId ||
              ""
          ).trim();

        if (!cleanId) {
          return rejectWithValue(
            {
              statusCode:
                400,

              message:
                "Consultation ID manquant.",

              error:
                "INVALID_CONSULTATION_ID",
            }
          );
        }

        console.group(
          "📤 UPDATE CONSULTATION"
        );

        console.log(
          "ID:",
          cleanId
        );

        console.log(
          "PAYLOAD:",
          consultationData
        );

        console.groupEnd();

        const response =
          await axiosConsultation.put(
            ENDPOINTS
              .CONSULTATION
              .BY_ID(
                cleanId
              ),

            consultationData
          );

        console.log(
          "✅ UPDATE CONSULTATION RESPONSE:",
          response.data
        );

        return response.data;
      } catch (error) {
        logApiError(
          "UPDATE CONSULTATION ERROR",
          error
        );

        return rejectWithValue(
          getErrorPayload(
            error,
            "Erreur lors de la mise à jour de la consultation"
          )
        );
      }
    }
  );

export const updateConsultationPayment = createAsyncThunk(
  "consultation/updatePayment",
  async ({ consultationId, consultationData }, { rejectWithValue }) => {
    try {
      const cleanId = String(consultationId || "").trim();
      if (!cleanId) {
        return rejectWithValue({
          statusCode: 400,
          message: "Consultation ID manquant.",
          error: "INVALID_CONSULTATION_ID",
        });
      }
      const response = await axiosConsultation.put(
        `${ENDPOINTS.CONSULTATION.BY_ID(cleanId)}/payment`,
        consultationData
      );
      return response.data;
    } catch (error) {
      logApiError("UPDATE CONSULTATION PAYMENT ERROR", error);
      return rejectWithValue(
        getErrorPayload(error, "Erreur lors de la mise à jour du paiement")
      );
    }
  }
);

/* =========================================================
   DELETE CONSULTATION
========================================================= */

export const deleteConsultation =
  createAsyncThunk(
    "consultation/delete",

    async (
      consultationId,
      {
        rejectWithValue,
      }
    ) => {
      try {
        const cleanId =
          String(
            consultationId ||
              ""
          ).trim();

        if (!cleanId) {
          return rejectWithValue(
            {
              statusCode:
                400,

              message:
                "Consultation ID manquant.",

              error:
                "INVALID_CONSULTATION_ID",
            }
          );
        }

        await axiosConsultation.delete(
          ENDPOINTS
            .CONSULTATION
            .BY_ID(
              cleanId
            )
        );

        console.log(
          "✅ CONSULTATION DELETED:",
          cleanId
        );

        return cleanId;
      } catch (error) {
        logApiError(
          "DELETE CONSULTATION ERROR",
          error
        );

        return rejectWithValue(
          getErrorPayload(
            error,
            "Erreur lors de la suppression de la consultation"
          )
        );
      }
    }
  );

/* =========================================================
   BACKWARD COMPATIBILITY
========================================================= */

export {
  getConsultationById as fetchConsultationById,
};

export {
  getConsultationsByPatient as fetchConsultationsByPatientId,
};

export {
  getConsultationsByDoctor as fetchConsultationsByDoctorId,
};